import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import {
  Building2, MapPin, Plus, Users, UserPlus, Copy, Check,
  CheckCircle2, Clock, XCircle, Lock,
} from "lucide-react";
import { StudioLayout } from "../components/layout/StudioLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { VenueCreateModal } from "../components/forms/VenueCreateModal";
import { useCurrentUser } from "../hooks/useCurrentUser";
import type { Id } from "convex/_generated/dataModel";

export function StudioDashboardPage() {
  const { user, isLoading } = useCurrentUser();
  const venues = useQuery(api.venues.getVenuesByOwner);
  const organizers = useQuery(api.studioOrganizers.getStudioOrganizers);
  const inviteByEmail = useMutation(api.studioOrganizers.inviteOrganizerByEmail);
  const generateCode = useMutation(api.studioOrganizers.generateInviteCode);
  const removeOrganizer = useMutation(api.studioOrganizers.removeOrganizer);

  const [showCreateVenue, setShowCreateVenue] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(user?.studioInviteCode ?? null);
  const [codeCopied, setCodeCopied] = useState(false);

  if (isLoading) {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </StudioLayout>
    );
  }

  if (!user || user.accountType !== "studio") {
    return (
      <StudioLayout>
        <div className="py-12 text-center">
          <p className="text-muted-foreground mb-4">
            This page is only available to studio accounts.
          </p>
          <Button asChild variant="outline">
            <Link href="/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </StudioLayout>
    );
  }

  const handleInviteByEmail = async () => {
    if (!inviteEmail.trim()) return;
    setInviteSending(true);
    setInviteError(null);
    setInviteSent(false);
    try {
      await inviteByEmail({ email: inviteEmail.trim() });
      setInviteEmail("");
      setInviteSent(true);
      setTimeout(() => setInviteSent(false), 3000);
    } catch (e: any) {
      setInviteError(e.message ?? "Failed to send invite.");
    } finally {
      setInviteSending(false);
    }
  };

  const handleGenerateCode = async () => {
    const code = await generateCode({});
    setInviteCode(code);
  };

  const handleCopyInviteLink = async () => {
    let code = inviteCode;
    if (!code) {
      code = await generateCode({});
      setInviteCode(code);
    }
    const link = `${window.location.origin}/join?code=${code}`;
    await navigator.clipboard.writeText(link);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  return (
    <StudioLayout>
      {/* ── Venues ── */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg text-foreground flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            My Venues
          </h2>
          <Button size="sm" onClick={() => setShowCreateVenue(true)}>
            <Plus className="w-4 h-4" />
            Add Venue
          </Button>
        </div>

        {venues === undefined ? (
          <div className="h-20 bg-secondary/30 rounded-lg animate-pulse" />
        ) : venues.length === 0 ? (
          <div className="text-center py-8 bg-card rounded-xl border border-border/50">
            <Building2 className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-muted-foreground mb-3">No venues yet.</p>
            <Button size="sm" onClick={() => setShowCreateVenue(true)}>
              <Plus className="w-4 h-4" />
              Register Your First Venue
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {venues.map((venue) => (
              <div
                key={venue._id}
                className="bg-card rounded-xl border border-border/50 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <Link
                    href={`/venue/${venue._id}`}
                    className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                  >
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-foreground">{venue.name}</h3>
                        {venue.claimStatus === "verified" && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-3 h-3" />
                            Verified
                          </span>
                        )}
                        {venue.claimStatus === "pending" && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <Clock className="w-3 h-3" />
                            Pending approval
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{venue.address}</p>
                    </div>
                  </Link>

                  {venue.claimStatus === "verified" && (
                    <span
                      className="flex-shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground"
                      title="Only you and organizers you authorize can post events here"
                    >
                      <Lock className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Yours to post</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Organizers ── */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg text-foreground flex items-center gap-2">
            <Users className="w-5 h-5" />
            Organizers
          </h2>
        </div>

        {/* Invite by email */}
        <div className="bg-card rounded-xl border border-border/50 p-5 mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" />
            Invite by Email
          </h3>
          <div className="flex gap-2">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="organizer@example.com"
              onKeyDown={(e) => e.key === "Enter" && handleInviteByEmail()}
            />
            <Button
              size="sm"
              onClick={handleInviteByEmail}
              disabled={inviteSending || !inviteEmail.trim()}
            >
              {inviteSent ? <Check className="w-4 h-4" /> : "Invite"}
            </Button>
          </div>
          {inviteError && (
            <p className="text-xs text-destructive mt-2">{inviteError}</p>
          )}
          {inviteSent && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">
              Invite sent!
            </p>
          )}
        </div>

        {/* Invite link */}
        <div className="bg-card rounded-xl border border-border/50 p-5 mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
            <Copy className="w-4 h-4 text-primary" />
            Shareable Invite Link
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Anyone with this link can join as an organizer for your studio.
          </p>
          {inviteCode && (
            <p className="text-xs font-mono bg-secondary/50 rounded px-3 py-2 mb-3 break-all">
              {window.location.origin}/join?code={inviteCode}
            </p>
          )}
          <Button size="sm" variant="outline" onClick={handleCopyInviteLink}>
            {codeCopied ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                {inviteCode ? "Copy Link" : "Generate & Copy Link"}
              </>
            )}
          </Button>
          {inviteCode && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-2 text-xs text-muted-foreground"
              onClick={handleGenerateCode}
            >
              Refresh Code
            </Button>
          )}
        </div>

        {/* Organizer list */}
        {organizers === undefined ? (
          <div className="h-16 bg-secondary/30 rounded-lg animate-pulse" />
        ) : organizers.length === 0 ? (
          <div className="text-center py-6 bg-card rounded-xl border border-border/50">
            <p className="text-sm text-muted-foreground">No organizers yet. Invite some above.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {organizers.map((org) => (
              <div
                key={org._id}
                className="flex items-center justify-between bg-card rounded-lg border border-border/50 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {org.organizerName ?? org.organizerEmail}
                  </p>
                  {org.organizerName && (
                    <p className="text-xs text-muted-foreground">{org.organizerEmail}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {org.status === "pending" && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                      Pending
                    </span>
                  )}
                  {org.status === "active" && (
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full">
                      Active
                    </span>
                  )}
                  <button
                    onClick={() => removeOrganizer({ studioOrganizerId: org._id })}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    title="Remove organizer"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {showCreateVenue && (
        <VenueCreateModal
          onClose={() => setShowCreateVenue(false)}
          onCreated={() => setShowCreateVenue(false)}
        />
      )}
    </StudioLayout>
  );
}
